import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "@/assets/logo-houselet.png";
import { formatDecimal } from "./formatting";
import { daRestituire } from "./pagamenti";
import { titoloDettaglio } from "./documentoPreventivo";
import { wrapAddress } from "./pdfWrap";

/* Unità di vendita di un articolo: mq se ha i mq/scatola, ml se ha i ml/confezione
   (per i battiscopa i ml hanno la precedenza), pezzi se non è venduto a scatola.

   [sc-48245] Simboli in minuscolo — "mq" e "ml", non "mQ" e "mL". Il resto
   dell'applicazione (colonne dei listini, form preventivo, suffisso del
   prezzo) li scriveva già così: il PDF era l'unico punto con una grafia sua,
   e finiva sotto gli occhi del cliente. */
function boxUnitOf(item, isBattiscopa, isArea) {
  const sqm = Number(item?.sqmPerBox || 0);
  const ml = Number(item?.mlPerBox || 0);
  if (isBattiscopa && ml > 0) return { perBox: ml, unit: "ml" };
  if (sqm > 0) return { perBox: sqm, unit: "mq" };
  if (ml > 0) return { perBox: ml, unit: "ml" };
  // articolo venduto a mq/ml ma senza il dato per scatola: si assume 1 per
  // scatola, come fanno il form e l'API (COALESCE(sqmPerBox, 1))
  if (isBattiscopa) return { perBox: 1, unit: "ml" };
  if (isArea) return { perBox: 1, unit: "mq" };
  return { perBox: 0, unit: "pz" };
}

/* Quantità da stampare per una riga venduta a scatola/confezione.
   Priorità ai mq/ml in corso di modifica nel form (mqOverrides), poi a quelli
   persistiti sulla riga (`mqQuantity`); in mancanza di entrambi si ricava dal
   numero di scatole (righe create prima del campo dedicato). */
function areaQuantity(itemId, boxes, perBox, mqOverrides, persistedMq) {
  const typed = Number(mqOverrides?.[itemId]);
  if (typed > 0) return Number(typed.toFixed(2));
  const saved = Number(persistedMq);
  if (saved > 0) return Number(saved.toFixed(2));
  return Number((Number(boxes || 0) * perBox).toFixed(2));
}

function buildQuoteTable(quote, pageWidth, marginX, mqOverrides = {}) {
  const head = [
    [
      "Cod.",
      "Descrizione",
      "Prezzo unitario",
      "Q.tà",
      "U.m.",
      "Imponibile",
      "IVA",
      "Totale",
    ],
  ];

  const lower = (v) => String(v || "").toLowerCase();

  const rows = [];
  let hasLineDiscount = false; // true se almeno una riga usa il prezzo scontato
  const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

  const AREA_CATS_PDF = [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ];

  (quote.items || []).forEach((it) => {
    const name = it.itemName || it.subBrand || it.brand || "-";
    const isArea = AREA_CATS_PDF.includes(lower(it.category));
    const isBattiscopa = lower(name).includes("battiscopa");

    // Articoli venduti a scatola/confezione: la quantità si esprime in mq/ml,
    // non in scatole (vale per tutto il gruppo area, non solo le piastrelle).
    const { perBox, unit } = boxUnitOf(it, isBattiscopa, isArea);
    const isBoxBased = perBox > 0;

    const rawQty =
      it?.QuoteItem?.quantity ??
      (isBoxBased ? it?.boxQuantity : it?.pieces) ??
      0;
    const baseQty = Number(rawQty);
    // [sc-49151] Il preventivo appena creato arriva dalla risposta di
    // createQuote, che ha `itemId` e non `id`: senza il ripiego i mq scritti
    // nel form (mqOverrides, indicizzati per itemId) non venivano trovati.
    const qty = isBoxBased
      ? areaQuantity(
          it.id ?? it.itemId,
          baseQty,
          perBox,
          mqOverrides,
          it?.QuoteItem?.mqQuantity ?? it?.mqQuantity
        )
      : baseQty;

    // Prezzo netto applicato (nuova semantica IVA esclusa): le righe storiche
    // migrate espongono il netto in discountedPrice; altrimenti è `price`.
    const dpThrough = it?.QuoteItem?.discountedPrice ?? it?.discountedPrice;
    const priceThrough = Number(it?.QuoteItem?.price ?? it.price ?? 0);
    const appliedUnitNet =
      dpThrough !== null && dpThrough !== undefined
        ? Number(dpThrough)
        : priceThrough;
    if (dpThrough !== null && dpThrough !== undefined) hasLineDiscount = true;
    // IVA della RIGA preventivo (through), poi fallback su articolo, poi 22.
    // Sempre ?? perché 0 (IVA 0%) è un valore valido.
    const iva = it?.QuoteItem?.iva ?? it?.iva ?? 22;
    const ivaRate = Number(iva ?? 0);
    // Importi di RIGA (quantità inclusa): imponibile, imposta, totale lordo.
    const taxableAmount = Number((appliedUnitNet * qty).toFixed(2));
    const taxAmount = Number(((taxableAmount * ivaRate) / 100).toFixed(2));
    const grossTotal = Number((taxableAmount + taxAmount).toFixed(2));

    const dim =
      it.formatLabel ||
      (it.widthCm && it.heightCm ? `${it.widthCm}×${it.heightCm}` : null);
    const normStr = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr = it.thicknessMm ? `${parseFloat(it.thicknessMm)}mm` : null;
    const specParts = isArea
      ? [
          dim && !normStr(name).includes(normStr(dim)) ? dim : null,
          it.surface,
          thickStr && !name.toLowerCase().includes(thickStr) ? thickStr : null,
        ].filter(Boolean)
      : [];
    const desc = specParts.length
      ? `${name} ${specParts.join(" ")}`.trim()
      : name;

    // [sc-48248] Suddivisione, nota e ordine scelti nel CRM: quando una
    // suddivisione c'è prende il posto della categoria come intestazione di
    // gruppo, perché è così che il preventivo è stato pensato per il cliente.
    const sez = String(it?.QuoteItem?.sectionName || "").trim();

    rows.push({
      group:
        sez.toUpperCase() ||
        String(it.category || "Generale")
          .trim()
          .toUpperCase(),
      daSuddivisione: Boolean(sez),
      code: it.supplierCode || it.itemCode || it.code || "-",
      desc,
      note: it?.QuoteItem?.note || null,
      position: it?.QuoteItem?.position ?? null,
      qty,
      unit,
      unitPrice: appliedUnitNet,
      taxable: taxableAmount,
      iva,
      tax: taxAmount,
      grossTotal,
    });
  });

  (quote.quote_items || []).forEach((qi) => {
    const id = qi?.item?.id;
    if (id && itemMap.has(id)) return;

    const it2 = qi?.item || {};
    const name = it2.itemName || "-";

    const isArea2 = AREA_CATS_PDF.includes(lower(it2.category));
    const isBattiscopa = lower(name).includes("battiscopa");

    // qi.quantity è in scatole: va convertita in mq/ml come nel blocco sopra,
    // altrimenti l'importo di riga (prezzo al mq × scatole) risulta errato.
    const { perBox, unit } = boxUnitOf(it2, isBattiscopa, isArea2);
    const qty =
      perBox > 0
        ? areaQuantity(it2.id, qi.quantity, perBox, mqOverrides, qi.mqQuantity)
        : Number(qi.quantity || 0);

    // Prezzo netto applicato (nuova semantica IVA esclusa).
    const dpQi = qi.discountedPrice;
    const appliedUnitNet =
      dpQi !== null && dpQi !== undefined
        ? Number(dpQi)
        : Number(qi.price || 0);
    if (dpQi !== null && dpQi !== undefined) hasLineDiscount = true;
    const iva = qi?.iva ?? 22;
    const ivaRate = Number(iva ?? 0);
    const taxableAmount = Number((appliedUnitNet * qty).toFixed(2));
    const taxAmount = Number(((taxableAmount * ivaRate) / 100).toFixed(2));
    const grossTotal = Number((taxableAmount + taxAmount).toFixed(2));

    const group = String(it2.category || "Generale")
      .trim()
      .toUpperCase();

    const dim2 =
      it2.formatLabel ||
      (it2.widthCm && it2.heightCm ? `${it2.widthCm}×${it2.heightCm}` : null);
    const normStr2 = (s) => (s || "").replace(/×/g, "x").toLowerCase();
    const thickStr2 = it2.thicknessMm
      ? `${parseFloat(it2.thicknessMm)}mm`
      : null;
    const specParts2 = isArea2
      ? [
          dim2 && !normStr2(name).includes(normStr2(dim2)) ? dim2 : null,
          it2.surface,
          thickStr2 && !name.toLowerCase().includes(thickStr2)
            ? thickStr2
            : null,
        ].filter(Boolean)
      : [];
    const desc2 = specParts2.length
      ? `${name} ${specParts2.join(" ")}`.trim()
      : name;

    const sez2 = String(qi?.sectionName || "").trim();

    rows.push({
      group: sez2.toUpperCase() || group,
      daSuddivisione: Boolean(sez2),
      code: it2.supplierCode || it2.itemCode || it2.code || "-",
      desc: desc2,
      note: qi?.note || null,
      position: qi?.position ?? null,
      qty,
      unit,
      unitPrice: appliedUnitNet,
      taxable: taxableAmount,
      iva,
      tax: taxAmount,
      grossTotal,
    });
  });

  // [sc-48248] L'ordine è quello deciso trascinando le righe nel CRM. Le righe
  // senza posizione (nessuna, dopo la migration) restano in fondo nell'ordine
  // in cui sono arrivate.
  const ordinate = rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const pa = a.r.position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.r.position ?? Number.MAX_SAFE_INTEGER;
      return pa === pb ? a.i - b.i : pa - pb;
    })
    .map(({ r }) => r);

  const order = [];
  const groups = {};
  ordinate.forEach((r) => {
    if (!groups[r.group]) {
      groups[r.group] = [];
      order.push(r.group);
    }
    groups[r.group].push(r);
  });

  const body = [];
  order.forEach((g) => {
    const righe = groups[g];
    // [sc-48248] Il totale si mostra solo sulle SUDDIVISIONI, non sui gruppi di
    // categoria: un preventivo che non le usa esce identico a prima. Serve a
    // dire di chi è il prezzo quando dentro al gruppo c'è un kit.
    const eSuddivisione = righe.some((r) => r.daSuddivisione);
    const totaleGruppo = righe.reduce(
      (somma, r) => somma + Number(r.grossTotal || 0),
      0
    );
    const stileIntestazione = {
      fillColor: [235, 235, 235],
      fontStyle: "bold",
    };

    body.push(
      eSuddivisione
        ? [
            {
              content: g,
              colSpan: 7,
              styles: { ...stileIntestazione, halign: "left" },
            },
            {
              content: `€ ${formatDecimal(totaleGruppo)}`,
              styles: { ...stileIntestazione, halign: "right" },
            },
          ]
        : [
            {
              content: g,
              colSpan: 8,
              styles: { ...stileIntestazione, halign: "left" },
            },
          ]
    );

    righe.forEach((r) => {
      // [sc-48248] Riga a zero dentro una suddivisione che un prezzo ce l'ha:
      // è compresa in quel prezzo. Scritto "€ 0,00" sembra un errore o un
      // regalo, e il cliente non ha modo di capire che è già pagata sopra.
      const compresa =
        eSuddivisione && totaleGruppo > 0 && Number(r.taxable) === 0;

      body.push([
        r.code,
        // [sc-48248] La nota è scritta per il cliente: va sotto la descrizione,
        // nella stessa cella, se no servirebbe una riga di tabella in più.
        // [sc-49209] Più piccola e più chiara, come l'imposta (`didDrawCell`).
        r.note
          ? { content: `${r.desc}\n— ${r.note}`, sotto: `— ${r.note}` }
          : r.desc,
        // [sc-49209] Prezzo unitario netto (IVA esclusa), lo stesso da cui
        // nasce l'imponibile: prezzo unitario × quantità = imponibile.
        compresa ? "" : `€ ${formatDecimal(r.unitPrice)}`,
        String(r.qty).replace(".", ","),
        r.unit,
        compresa ? "incluso" : `€ ${formatDecimal(r.taxable)}`,
        // [sc-49209] Aliquota e imposta in un'unica colonna, su due righe,
        // per fare posto al prezzo unitario. Il testo ha due righe perché la
        // cella sia alta il giusto; l'imposta la ridisegna `didDrawCell`, più
        // piccola e più chiara (autoTable ha un solo stile per cella).
        compresa
          ? ""
          : {
              content: `${r.iva}%\n€ ${formatDecimal(r.tax)}`,
              sotto: `€ ${formatDecimal(r.tax)}`,
            },
        compresa ? "" : `€ ${formatDecimal(r.grossTotal)}`,
      ]);
    });
  });

  const usable = pageWidth - 2 * marginX;
  // [sc-49209] Per fare posto al prezzo unitario aliquota e imposta stanno
  // in un'unica colonna «IVA», e «Cod.» si stringe quando nessuna riga ha un
  // codice: lo spazio guadagnato va alla descrizione.
  const conCodice = rows.some((r) => r.code && r.code !== "-");
  // Le intestazioni stanno su una riga sola (headStyles a 9pt): «Prezzo
  // unitario» chiede 26 mm, Imponibile e Totale tengono «€ 12.345,67» (21 mm).
  const w = {
    cod: conCodice ? 0.09 : 0.06,
    unitPrice: 0.14,
    qty: 0.07,
    um: 0.06,
    taxable: 0.13,
    iva: 0.1,
    totale: 0.13,
  };
  const cw = {
    0: Math.floor(usable * w.cod),
    2: Math.floor(usable * w.unitPrice),
    3: Math.floor(usable * w.qty),
    4: Math.floor(usable * w.um),
    5: Math.floor(usable * w.taxable),
    6: Math.floor(usable * w.iva),
    7: Math.floor(usable * w.totale),
  };

  const rowsTaxableTotal = Number(
    rows.reduce((s, r) => s + Number(r.taxable || 0), 0).toFixed(2)
  );
  const rowsTaxTotal = Number(
    rows.reduce((s, r) => s + Number(r.tax || 0), 0).toFixed(2)
  );
  const rowsItemsTotal = Number(
    rows.reduce((s, r) => s + Number(r.grossTotal || 0), 0).toFixed(2)
  );

  // [sc-49209] Testo secondario di una cella (`raw.sotto`): l'imposta sotto
  // l'aliquota e la nota sotto la descrizione. autoTable ha un solo stile
  // per cella, quindi la cella contiene tutto il testo (così è alta abbastanza)
  // ma ne scrive solo la parte principale; il secondario si disegna sotto,
  // a 8pt e in grigio, andando a capo sulla larghezza della cella.
  const PICCOLO = 8;
  const willDrawCell = (data) => {
    const { cell } = data;
    if (data.section !== "body" || !cell.raw?.sotto) return;
    const primaRiga = String(cell.raw.sotto).split("\n")[0];
    const inizio = cell.text.findIndex((l) => {
      const t = String(l).trim();
      return t !== "" && primaRiga.startsWith(t);
    });
    cell.righePrincipali = inizio > 0 ? inizio : 1;
    cell.text = cell.text.slice(0, cell.righePrincipali);
  };
  const didDrawCell = (data) => {
    const { cell, doc } = data;
    if (data.section !== "body" || !cell.raw?.sotto) return;
    const k = doc.internal.scaleFactor;
    const corpo = cell.styles.fontSize / k;
    const piccolo = PICCOLO / k;
    const larghezza = cell.width - cell.padding("horizontal");
    const aDestra = cell.styles.halign === "right";
    const x = aDestra
      ? cell.x + cell.width - cell.padding("right")
      : cell.x + cell.padding("left");
    // Linea di base dell'ultima riga principale, poi una riga piccola sotto.
    let y =
      cell.y +
      cell.padding("top") +
      corpo * 0.85 +
      (cell.righePrincipali - 1) * corpo * 1.15 +
      piccolo * 1.15;
    doc.setFontSize(PICCOLO).setTextColor(110);
    doc.splitTextToSize(cell.raw.sotto, larghezza).forEach((riga) => {
      doc.text(riga, x, y, aDestra ? { align: "right" } : undefined);
      y += piccolo * 1.15;
    });
    doc.setFontSize(cell.styles.fontSize).setTextColor(0);
  };

  return {
    head,
    body,
    willDrawCell,
    didDrawCell,
    hasLineDiscount,
    rowsTaxableTotal,
    rowsTaxTotal,
    rowsItemsTotal,
    tableWidth: usable,
    margin: { left: marginX, right: marginX },
    columnStyles: {
      0: { cellWidth: cw[0], halign: "left" },
      1: { cellWidth: "auto", minCellWidth: 30, halign: "left" },
      2: { cellWidth: cw[2], halign: "right" },
      3: { cellWidth: cw[3], halign: "right" },
      4: { cellWidth: cw[4], halign: "center" },
      5: { cellWidth: cw[5], halign: "right" },
      6: { cellWidth: cw[6], halign: "right" },
      7: { cellWidth: cw[7], halign: "right" },
    },
  };
}

/**
 * @param {object} params
 * @param {object} params.quote         - dati preventivo/ordine completi
 * @param {object} params.toast         - istanza vue-toastification ($toast)
 * @param {Function} params.uploadQuoteDocument - fn(payload) che esegue il dispatch Vuex
 * @param {Function} [params.onComplete]        - callback dopo upload riuscito
 */
export async function generateQuotePdf({
  quote,
  toast,
  uploadQuoteDocument,
  onComplete,
  isOrder = false,
  filenameOverride = null,
  mqOverrides = {},
}) {
  try {
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 10;
    const marginY = 10;
    let y = marginY;

    if (logoPath) {
      try {
        doc.addImage(logoPath, "PNG", marginX - 5, y, 60, 24);
        // eslint-disable-next-line no-empty
      } catch {}
    }
    doc.setFontSize(9).setFont("helvetica", "normal");
    [
      "Concept Home distribution S.R.L",
      "Via Salvatore Averna, 4",
      "95045 - Misterbianco (CT)",
      "T.095 8164002 - 3316557313",
    ].forEach((text, i) => {
      const x = pageWidth - marginX - doc.getTextWidth(text);
      doc.text(text, x, y + 5 + i * 4);
    });
    y += 48;

    const printLabelValue = (
      label,
      value,
      xPos,
      yPos,
      maxWidth = null,
      link = null
    ) => {
      const labelCap = label.charAt(0).toUpperCase() + label.slice(1);
      doc.setFont("helvetica", "bold");
      doc.text(`${labelCap}:`, xPos, yPos);
      const off = doc.getTextWidth(`${labelCap}: `) + 0.8;
      doc.setFont("helvetica", "normal");
      const val = String(value || "N/D");
      // [sc-49170] Con una larghezza massima il valore va a capo, allineato
      // sotto il valore e senza spezzare le parti dell'indirizzo, invece di
      // essere troncato coi puntini: l'indirizzo di consegna usciva a metà.
      const lines = maxWidth
        ? wrapAddress(val, maxWidth - off, (t) => doc.getTextWidth(t))
        : [val];
      lines.forEach((line, i) => {
        const lineY = yPos + i * 4;
        if (link) {
          doc.setTextColor(0, 0, 255);
          doc.textWithLink(line, xPos + off, lineY, { url: link });
          doc.setTextColor(0);
        } else {
          doc.text(line, xPos + off, lineY);
        }
      });
      return yPos + lines.length * 4;
    };

    const contactValue = quote?.Client?.pec || quote?.Client?.email || "";
    const contactLabel = quote?.Client?.pec ? "pec" : "email";
    const lh = (() => {
      const raw = quote?.Client?.legalHeadquarters;
      if (!raw) return {};
      if (typeof raw === "object") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    })();
    const rightX = pageWidth / 2 - 10;
    const colW = pageWidth - rightX - marginX;
    const leftColW = rightX - marginX - 10;
    const formattedDate = new Date(quote.date).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // Ultimo pagamento usato per generare il PDF. Calcolato qui (prima del
    // blocco "Dettaglio ordine") perché serve sia per la data documento sia,
    // più in basso, per la riga di riepilogo e i dettagli pagamento.
    const lastPayment = [...(quote.payments || [])].sort((a, b) => {
      const da = new Date(a?.paidAt ?? a?.createdAt ?? 0).getTime();
      const dbb = new Date(b?.paidAt ?? b?.createdAt ?? 0).getTime();
      if (da !== dbb) return da - dbb;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    })[(quote.payments || []).length - 1];

    const formattedDocDate = lastPayment
      ? new Date(
          lastPayment.paidAt ?? lastPayment.createdAt ?? Date.now()
        ).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : null;

    // LEFT: Dettaglio cliente
    let leftX = marginX;
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text("Dettaglio cliente", leftX, y);
    let yLeft = y + 8;
    doc.setFontSize(9);
    yLeft = printLabelValue("cliente", quote?.Client?.clientName, leftX, yLeft);
    yLeft = printLabelValue(
      "telefono",
      quote?.Client?.phoneNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(contactLabel, contactValue, leftX, yLeft);
    yLeft = printLabelValue(
      "partita iva",
      quote?.Client?.vatNumber,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "codice fiscale",
      quote?.Client?.taxCode,
      leftX,
      yLeft
    );
    yLeft = printLabelValue(
      "azienda",
      quote?.Client?.companyName,
      leftX,
      yLeft
    );
    yLeft = printLabelValue("sede legale", lh.address, leftX, yLeft, leftColW);

    // RIGHT: Dettaglio preventivo
    // [sc-49133] Con il numero: «Dettaglio ordine #37». È lo stesso id del
    // preventivo, perché un ordine è il preventivo confermato.
    doc
      .setFontSize(12)
      .setFont("helvetica", "bold")
      .text(titoloDettaglio(quote, isOrder), rightX, y);
    let yRight = y + 8;
    doc.setFontSize(9);
    if (lastPayment) {
      yRight = printLabelValue("data ordine", formattedDate, rightX, yRight);
      yRight = printLabelValue(
        "data documento",
        formattedDocDate,
        rightX,
        yRight
      );
    } else {
      yRight = printLabelValue("data", formattedDate, rightX, yRight);
    }
    // Modalità di evasione: in "ritiro" non c'è indirizzo di consegna.
    const isPickup = quote?.deliveryType === "ritiro";
    if (isPickup) {
      yRight = printLabelValue("modalità", "Ritiro", rightX, yRight, colW);
    } else {
      yRight = printLabelValue(
        "indirizzo di consegna",
        quote?.location?.address,
        rightX,
        yRight,
        colW,
        quote?.location?.link || null
      );
    }

    y = Math.max(yLeft, yRight) + 4;
    doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
    y += 6;

    const tbl = buildQuoteTable(quote, pageWidth, marginX, mqOverrides);
    autoTable(doc, {
      startY: y,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 1.5 },
      // [sc-49209] Intestazioni a 9pt, su una riga sola (larghezze in buildQuoteTable).
      headStyles: {
        fillColor: [200, 200, 200],
        fontStyle: "bold",
        fontSize: 9,
      },
      head: tbl.head,
      body: tbl.body,
      columnStyles: tbl.columnStyles,
      tableWidth: tbl.tableWidth,
      margin: tbl.margin,
      // [sc-49209] Una riga non si spezza fra due pagine: il testo secondario
      // (nota, imposta) si disegna sotto quello principale della stessa cella.
      rowPageBreak: "avoid",
      willDrawCell: tbl.willDrawCell,
      didDrawCell: tbl.didDrawCell,
    });
    y = (doc.lastAutoTable?.finalY || y) + 10;

    // Spese di imballo: addebitate solo se il preventivo ha il flag attivo;
    // l'importo è storicizzato in packagingFeeAmount (niente valore cablato).
    // Lo sconto % NON deve gravare sull'imballo, per coerenza col totale server.
    const packagingFee = quote.hasPackagingFee
      ? Number(quote.packagingFeeAmount || 0)
      : 0;
    // [sc-48735] La maggiorazione è una riga del conto, non parte del totale
    // articoli. Prima, con una maggiorazione, il totale articoli era
    // `realTotalPrice`, che ha già tolto il pagato e aggiunto l'imballo: nel
    // riepilogo sconto e imballo contavano due volte e il pagato si sottraeva
    // due volte. Ora il conto segue il totale dovuto del server:
    // articoli − sconto + maggiorazione + imballo.
    const surcharge = Number(quote.surcharge || 0);
    const surchargePercent = Number(quote.surcharge_percent || 0);
    // Se almeno una riga ha un prezzo scontato, il totale articoli deriva dalla
    // somma delle righe (già scontate) per coerenza con la tabella; altrimenti
    // resta il totale calcolato lato server (comportamento invariato).
    const itemsTotal =
      tbl.hasLineDiscount && tbl.rowsItemsTotal > 0
        ? tbl.rowsItemsTotal
        : Number(quote.totalPrice || 0);
    const percent = Number(quote.discount_percent || 0);
    const discountFromCol = Number(quote.discount || 0);
    const discountCalc = Number(((itemsTotal * percent) / 100).toFixed(2));
    const discount = discountFromCol > 0 ? discountFromCol : discountCalc;
    const afterDiscount = Math.max(
      0,
      Number((itemsTotal - discount + surcharge + packagingFee).toFixed(2))
    );
    const deposit = Number(quote.deposit || 0);
    const toPay = Math.max(0, Number((afterDiscount - deposit).toFixed(2)));
    // [sc-48735] Pagato più del dovuto (una modifica ha abbassato il totale):
    // il rimborso avviene fuori dal gestionale, qui lo si rende visibile.
    const credito = daRestituire(afterDiscount - deposit);

    // Etichette dei tipi di pagamento. Senza storico si usa quella
    // dell'ultimo pagamento per il versato cumulativo (deposit).
    const PAYMENT_TYPE_LABELS = {
      acconto: "Acconto",
      saldo_parziale: "Saldo parziale",
      saldo_finale: "Saldo finale",
    };
    const depositLabel = PAYMENT_TYPE_LABELS[lastPayment?.type] || "Acconto";

    const headPadTop = 10;
    const lineH = 6;

    // Formattazione display in euro in stile italiano (migliaia con punto,
    // decimali con virgola)
    const eur = (n) => formatDecimal(n);

    // ---- RIEPILOGO come un conto [sc-48735] ----
    // Ogni riga: label, amount, sign ("+" | MENO), tone ("muted" | "strong"),
    // indent (dettaglio pagamenti), oppure { rule: true } per una linea.
    // Il meno è un trattino medio (U+2013): il segno matematico «−» (U+2212)
    // non è nei font standard di jsPDF e uscirebbe come un carattere a caso.
    const MENO = "–";
    const dataBreve = (d) => {
      const t = new Date(d);
      return Number.isNaN(t.getTime())
        ? ""
        : t.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          });
    };

    // Imponibile e IVA sono il dettaglio del totale articoli, non addendi:
    // grigi e senza segno.
    const leftRows = [
      { label: "Imponibile", amount: tbl.rowsTaxableTotal, tone: "muted" },
      { label: "IVA", amount: tbl.rowsTaxTotal, tone: "muted" },
      { label: "Totale articoli", amount: itemsTotal },
    ];
    if (surcharge > 0) {
      const pctLabel = surchargePercent ? ` (${surchargePercent}%)` : "";
      leftRows.push({
        label: `Maggiorazione${pctLabel}`,
        amount: surcharge,
        sign: "+",
      });
    }
    if (packagingFee > 0) {
      leftRows.push({
        label: "Spese imballo",
        amount: packagingFee,
        sign: "+",
      });
    }
    if (discount > 0) {
      const pctLabel = percent ? ` (${percent}%)` : "";
      leftRows.push({
        label: `Sconto${pctLabel}`,
        amount: discount,
        sign: MENO,
      });
    }
    leftRows.push({ rule: true });
    leftRows.push({
      label: isOrder ? "Totale ordine" : "Totale preventivo",
      amount: afterDiscount,
      tone: "strong",
    });

    if (deposit > 0) {
      // Storico completo, dal più vecchio, con la data.
      const pagamenti = [...(quote.payments || [])]
        .sort((a, b) => {
          const da = new Date(a.paidAt ?? a.createdAt ?? 0).getTime();
          const db = new Date(b.paidAt ?? b.createdAt ?? 0).getTime();
          return da - db;
        })
        .filter((p) => Number(p.amount || 0) > 0)
        .map((p) => ({
          label: `${PAYMENT_TYPE_LABELS[p.type] || "Acconto"} ${dataBreve(
            p.paidAt ?? p.createdAt
          )}`.trim(),
          amount: Number(p.amount),
        }));

      if (pagamenti.length > 1) {
        // Più versamenti: il dettaglio in grigio, il totale che si sottrae.
        pagamenti.forEach((r) =>
          leftRows.push({ ...r, tone: "muted", indent: true })
        );
        leftRows.push({ label: "Totale pagato", amount: deposit, sign: MENO });
      } else if (pagamenti.length === 1) {
        // Uno solo: è lui la riga che si sottrae.
        leftRows.push({ ...pagamenti[0], sign: MENO });
      } else {
        // Nessun dettaglio, ma un versato cumulativo.
        leftRows.push({ label: depositLabel, amount: deposit, sign: MENO });
      }

      leftRows.push({ rule: true });
      leftRows.push(
        credito > 0
          ? {
              label: "Da restituire al cliente",
              amount: credito,
              tone: "strong",
            }
          : { label: "Totale da pagare", amount: toPay, tone: "strong" }
      );
    }

    // ---- Dettagli pagamento: etichetta grigia sopra, valore sotto ----
    const PAYMENT_METHOD_LABELS = {
      contanti: "Contanti",
      bonifico: "Bonifico",
      "carta di credito": "Carta di credito",
    };
    const datiBancari = [
      { label: "Banca", value: "Monte dei Paschi di Siena" },
      { label: "IBAN", value: "IT69R0103084070000001512488" },
      { label: "Intestato a", value: "Concept Home distribution S.R.L" },
    ];
    // Metodo pagamento mostrato solo se presente sull'ultimo pagamento.
    if (lastPayment?.method) {
      const methodKey = String(lastPayment.method || "").toLowerCase();
      datiBancari.unshift({
        label: "Metodo doc. corrente",
        value: PAYMENT_METHOD_LABELS[methodKey] || lastPayment.method,
      });
    }
    const BANCA_LABEL_PT = 8;
    const BANCA_VALUE_PT = 9;
    const BANCA_LABEL_TO_VALUE = 4.5; // dall'etichetta al suo valore
    const BANCA_VALUE_TO_LABEL = 6.5; // dal valore all'etichetta successiva

    const leftTitle = "Riepilogo";
    const centerTitle = "Dettagli pagamento";
    const rightTitle = "Firma";
    const sigLineYOffset = 12;

    // Griglia: Riepilogo 40% | Dettagli pagamento 38% | Firma 22%
    const QUOTE_COLONNE = [0.4, 0.38];
    const colPad = 6; // margine interno di ogni colonna
    const mm = (n) => Math.round(n * 10) / 10; // bordi a 0,1 mm, niente residui float
    const larghezza = pageWidth - 2 * marginX;
    const bordo1 = mm(marginX + larghezza * QUOTE_COLONNE[0]);
    const bordo2 = mm(bordo1 + larghezza * QUOTE_COLONNE[1]);
    const col1X = marginX + colPad;
    const col1Right = bordo1 - colPad;
    const col2X = bordo1 + colPad;
    const col3X = bordo2 + colPad;
    const sigEnd = pageWidth - marginX - colPad;
    const ruleH = 3;

    const fontDi = (row) => {
      if (row.tone === "strong")
        doc.setFont("helvetica", "bold").setFontSize(10.5);
      else if (row.tone === "muted")
        doc.setFont("helvetica", "normal").setFontSize(9);
      else doc.setFont("helvetica", "normal").setFontSize(10);
    };
    const importoDi = (row) =>
      `${row.sign ? `${row.sign} ` : ""}€ ${eur(row.amount)}`;

    // Una voce lunga va a capo; l'importo resta sulla sua prima riga.
    // Si misura prima di disegnare: l'altezza del riquadro dipende dagli a capo.
    const layout = leftRows.map((row) => {
      if (row.rule) return { row, h: ruleH };
      fontDi(row); // la larghezza dipende dal font: va impostato prima di misurare
      const x = col1X + (row.indent ? 3 : 0);
      const spazio = col1Right - x - doc.getTextWidth(importoDi(row)) - 3;
      const righe = doc.splitTextToSize(row.label, spazio);
      return { row, x, righe, h: righe.length * lineH };
    });
    const leftBody = layout.reduce((tot, l) => tot + l.h, 0);

    // Sotto l'ultima riga di testo restano 8 mm. Si toglie UNA riga (lineH),
    // non l'altezza dell'ultima voce: se va a capo, anche la sua seconda riga
    // deve stare dentro il riquadro.
    const leftHeight = headPadTop + 7 + leftBody - lineH + 8;
    const corpoCentro =
      datiBancari.length * BANCA_LABEL_TO_VALUE +
      (datiBancari.length - 1) * BANCA_VALUE_TO_LABEL;
    const centerHeight = headPadTop + 7 + corpoCentro + 8;
    const rightHeight = headPadTop + 2 + 7 + sigLineYOffset + 8;
    const minBlockH = 40;
    const blockH = Math.max(minBlockH, leftHeight, centerHeight, rightHeight);

    // Salto pagina: con molti pagamenti il riquadro supera i 55 mm di prima,
    // quindi si confronta anche con la sua altezza reale.
    const minVisualRoom = 55;
    if (pageHeight - y < Math.max(minVisualRoom, blockH + 5)) {
      doc.addPage();
      y = marginY;
    }

    doc.setFillColor(240, 240, 240);
    doc.rect(marginX, y, larghezza, blockH, "F");

    // Separatori verticali, staccati 5 mm dai bordi del riquadro
    doc.setDrawColor(200);
    [bordo1, bordo2].forEach((x) => {
      doc.line(x, y + 5, x, y + blockH - 5);
    });

    // Titoli sulla stessa riga, ciascuno a sinistra della sua colonna
    const titoloY = y + headPadTop;
    doc.setTextColor(0);
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(leftTitle, col1X, titoloY);
    doc.text(centerTitle, col2X, titoloY);
    doc.text(rightTitle, col3X, titoloY);

    // Colonna 1 — Riepilogo
    let fyLeft = titoloY + 7;
    layout.forEach(({ row, x, righe, h }) => {
      if (row.rule) {
        // la linea sta a metà dello spazio che le è riservato
        const ly = fyLeft - lineH / 2 + ruleH / 2;
        doc.setDrawColor(170);
        doc.line(col1X, ly, col1Right, ly);
        fyLeft += h;
        return;
      }
      fontDi(row);
      doc.setTextColor(row.tone === "muted" ? 110 : 0);
      doc.text(righe, x, fyLeft);
      doc.text(importoDi(row), col1Right, fyLeft, { align: "right" });
      fyLeft += h;
    });

    // Colonna 2 — Dettagli pagamento: la prima etichetta sta alla riga di
    // «Imponibile»
    let fb = titoloY + 7;
    datiBancari.forEach(({ label, value }, i) => {
      if (i > 0) fb += BANCA_VALUE_TO_LABEL;
      doc.setFont("helvetica", "normal").setFontSize(BANCA_LABEL_PT);
      doc.setTextColor(110);
      doc.text(label, col2X, fb);
      fb += BANCA_LABEL_TO_VALUE;
      doc.setFontSize(BANCA_VALUE_PT);
      doc.setTextColor(0);
      doc.text(String(value), col2X, fb);
    });

    // Colonna 3 — Firma: titolo in riga con gli altri, linea in fondo al
    // riquadro all'altezza dell'ultima riga del riepilogo
    const sigLineY = y + blockH - 8;
    doc.setDrawColor(0);
    doc.line(col3X, sigLineY, sigEnd, sigLineY);

    // colori di default per il resto del documento
    doc.setTextColor(0);
    doc.setDrawColor(0);

    y += blockH + 5;

    // ---- NOTE DEL PREVENTIVO ----
    if (quote.technical_queries) {
      const noteText = String(quote.technical_queries).trim();
      if (noteText) {
        doc.setFont("helvetica", "bold").setFontSize(9);
        doc.text("Note:", marginX, y);
        y += 5;
        doc.setFont("helvetica", "normal").setFontSize(8);
        const wrappedNote = doc.splitTextToSize(
          noteText,
          pageWidth - 2 * marginX
        );
        wrappedNote.forEach((ln) => {
          if (pageHeight - y < 10) {
            doc.addPage();
            y = marginY;
          }
          doc.text(ln, marginX, y);
          y += 4.5;
        });
        y += 4;
      }
    }

    // ---- CONDIZIONI DI PAGAMENTO ----
    const condizioni = [
      "1) Termini di pagamento: 40% anticipato all'ordine, saldo ad avviso merce pronta ns. deposito.",
      "2) Condizioni generali di vendita: L'autorizzazione al reso, o alla sostituzione, per quanto non conforme",
      "   all'ordine, deve essere preventivamente autorizzata dal produttore.",
      "3) Spese di deposito: Nel caso di mancato pagamento e/o ritardo nel ritiro e/o ingiustificato rifiuto della",
      "   merce da parte del cliente il venditore si riserva il diritto di addebitare al cliente i costi di deposito",
      "   della merce ex art. 1781 c.c. in misura di 30€ per ogni giorno di ritardo dalla data di presunta consegna.",
    ];
    const condLineH = 4.5;
    const condBlockH = 6 + condizioni.length * condLineH + 4;
    if (pageHeight - y < condBlockH) {
      doc.addPage();
      y = marginY;
    }
    doc.setTextColor(150).setFont("helvetica", "bold").setFontSize(9);
    doc.text("Condizioni di pagamento:", marginX, y);
    y += 5;
    doc.setFont("helvetica", "normal").setFontSize(8);
    condizioni.forEach((ln) => {
      doc.text(ln, marginX, y);
      y += condLineH;
    });
    doc.setTextColor(0);
    y += 4;

    const filenameBase = `${isOrder ? "Ordine" : "Preventivo"}_N_${
      quote.id
    }.pdf`;
    const isDev =
      process.env.NODE_ENV === "development" ||
      import.meta?.env?.MODE === "development" ||
      process.env?.VUE_APP_NODE_ENV === "development";

    const withSuffixBeforeExt = (filename, suffix) => {
      if (!suffix) return filename;
      const dot = filename.lastIndexOf(".");
      if (dot <= 0) return `${filename}.${suffix}`;
      return `${filename.slice(0, dot)}.${suffix}${filename.slice(dot)}`;
    };

    // Override esplicito (es. PDF pagamento "tipologia_YYYY-MM-DD_HH-mm.pdf"):
    // usato così com'è, senza suffisso ".dev". Senza override resta il
    // comportamento esistente del documento principale.
    const finalName = filenameOverride
      ? filenameOverride
      : withSuffixBeforeExt(filenameBase, isDev ? "dev" : "");
    const blob = doc.output("blob");
    const file = new File([blob], finalName, { type: "application/pdf" });

    try {
      await uploadQuoteDocument({
        quoteId: quote.id,
        document: file,
        filename: finalName,
      });
      toast.success("PDF generato e caricato con successo!");
      doc.save(finalName);
    } catch (error) {
      console.error("Errore durante l'upload del PDF:", error);
      toast.error(
        error.response?.data?.details ||
          error.response?.data?.error ||
          "Errore durante il caricamento del PDF."
      );
      return;
    }

    if (onComplete) await onComplete();
  } catch (error) {
    toast.error(
      error?.response?.data?.details ||
        error?.response?.data?.error ||
        "Errore durante la generazione/caricamento del PDF."
    );
  }
}
